import type { FC, KeyboardEvent, ReactNode, SVGProps } from "react"
import { useEffect, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { IconBookOpen4Fill18, IconTextAlignLeftFill18 } from "nucleo-ui-fill-18"
import {
	IconArrowRightOutline18 as ArrowRight,
	IconArrowUpOutline18 as ArrowUp,
	IconCheckOutline18 as Check,
	IconDatabaseOutline18 as Database,
	IconGlobeOutline18 as Globe,
	IconChatBubbleSparkleOutline18,
	IconMagnifierOutline18 as Magnifier,
	IconMessagesOutline18 as Messages,
	IconSquaresConnectedOutline18 as SquaresConnected,
	IconWrenchOutline18 as Wrench,
	IconXmarkOutline18 as Xmark,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Avatar,
	badgeVariants,
	Button,
	IconButton,
	Loader,
	Sheet,
	SheetClose,
	SheetContent,
	SheetTitle,
	Textarea,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { RouterInputs, RouterOutputs } from "@/lib/api"
import type { ImproveReplyTurn } from "@/lib/states/improve-reply-turns-state"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import {
	useImproveReplyTurns,
	useImproveReplyTurnsState,
} from "@/lib/states/improve-reply-turns-state"
import { useImprovedRepliesState } from "@/lib/states/improved-replies-state"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { DocsProposalCards } from "../../right-bar/ai-chat/docs-proposal-cards"
import { docsProposalChanges } from "../../right-bar/ai-chat/docs-proposal-outcomes"
import { AgentMessageAvatar } from "./base/agent-message-avatar"

type Icon = FC<SVGProps<SVGSVGElement>>
export type AgentReplyChannel = NonNullable<
	RouterInputs["agentReplies"]["context"]["channel"]
>
type ReplyContext = RouterOutputs["agentReplies"]["context"]
type ReplySource = ReplyContext["sources"][number]
type ReplyLookup = ReplyContext["lookups"][number]

export const IMPROVE_REPLY_COPY = {
	button: "Improve reply",
	improved: "Reply improved",
	drafting: "Drafting a fix",
	title: "Improve reply",
	customer: "Customer",
	asked: "asked",
	replied: "Agent replied",
	handedOff: "Handed to the team",
	searchedDocs: "Searched docs",
	searchedChangelog: "Searched changelog",
	searchedRoadmap: "Searched roadmap",
	used: "Used",
	ranQuery: "Ran a query",
	checkedRequests: "Checked past requests",
	readPage: "Read a web page",
	toolFailed: "Tool failed",
	noResults: "Returned nothing",
	failed: "Failed",
	more: (count: number) => `+${count} more`,
	empty: "The agent saved no sources or lookups for this reply.",
	ask: "What should it have said?",
	answerPlaceholder: "Write the answer you would give the customer",
	followUpPlaceholder: "Ask for a change to this draft",
	send: "Send",
	editing: (title: string) => `Editing ${title}`,
	draftingDocs: "Drafting a docs fix",
	noChange: "The docs already cover this",
	fixReady: "Docs fix ready",
	open: "Open",
	replaced: "Replaced by a newer version",
	skipped: "Skipped",
	published: "Published",
	undo: "Undo",
	addRule: "Add rule",
	ruleAdded: "Added to Workspace guidance",
	ruleFailed: "Your rule didn't save",
	seeGuidance: "See all",
	draftFailed: "Couldn't start the docs fix",
	skipFailed: "Couldn't skip that change",
	loadFailed: "Couldn't load what the agent used",
} as const

const plural = (count: number, one: string, many: string) =>
	`${count} ${count === 1 ? one : many}`

const SEARCH_KINDS = new Set<ReplyLookup["kind"]>([
	"docs",
	"changelog",
	"roadmap",
	"web",
])

export function summarizeReply(context: ReplyContext): string {
	const searches = context.lookups.filter((l) => SEARCH_KINDS.has(l.kind))
	const calls = context.lookups.filter((l) => l.kind === "connector")
	return [
		searches.length ? plural(searches.length, "search", "searches") : null,
		calls.length
			? plural(calls.length, "connector call", "connector calls")
			: null,
		context.sources.length
			? `${plural(context.sources.length, "source", "sources")} cited`
			: null,
	]
		.filter(Boolean)
		.join(" · ")
}

const SOURCE_HREF: Record<ReplySource["kind"], string> = {
	docs: "/edit-docs/",
	changelog: "/edit-changelog/",
	"roadmap-project": "/projects/",
	issue: "/issues/",
}

export const sourceHref = (source: ReplySource) =>
	`${SOURCE_HREF[source.kind]}${source.id}`

// A labelled connector step reads "Stripe: list_invoices".
function splitConnector(query: string | null) {
	const at = query?.indexOf(": ") ?? -1
	return query && at > 0
		? { name: query.slice(0, at), tool: query.slice(at + 2) }
		: { name: query, tool: null }
}

interface Heading {
	icon: Icon
	title: string
	detail: string | null
	mono?: boolean
}

function lookupHeading(lookup: ReplyLookup): Heading {
	const quoted = lookup.query ? `"${lookup.query}"` : null
	switch (lookup.kind) {
		case "docs":
			return {
				icon: Magnifier,
				title: IMPROVE_REPLY_COPY.searchedDocs,
				detail: quoted,
			}
		case "changelog":
			return {
				icon: Magnifier,
				title: IMPROVE_REPLY_COPY.searchedChangelog,
				detail: quoted,
			}
		case "roadmap":
			return {
				icon: Magnifier,
				title: IMPROVE_REPLY_COPY.searchedRoadmap,
				detail: quoted,
			}
		case "web":
			return {
				icon: Globe,
				title: IMPROVE_REPLY_COPY.readPage,
				detail: lookup.query,
			}
		case "connector": {
			const { name, tool } = splitConnector(lookup.query)
			return {
				icon: SquaresConnected,
				title: name
					? `${IMPROVE_REPLY_COPY.used} ${name}`
					: IMPROVE_REPLY_COPY.used,
				detail: tool,
				mono: true,
			}
		}
		case "database":
			return {
				icon: Database,
				title: IMPROVE_REPLY_COPY.ranQuery,
				detail: lookup.query,
				mono: true,
			}
		case "requests":
			return {
				icon: Messages,
				title: IMPROVE_REPLY_COPY.checkedRequests,
				detail: null,
			}
		default:
			return {
				icon: Wrench,
				title: IMPROVE_REPLY_COPY.toolFailed,
				detail: lookup.tool,
				mono: true,
			}
	}
}

const MARKER =
	"flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white text-secondary ring-1 ring-inset ring-black/10 dark:bg-gray-800 dark:ring-white/10"

function Step({
	marker,
	title,
	detail,
	mono,
	muted,
	last,
	children,
}: {
	marker: ReactNode
	title: string
	detail?: string | null
	mono?: boolean
	muted?: boolean
	last?: boolean
	children?: ReactNode
}) {
	return (
		<div className="flex gap-3" data-testid="improve-reply-step">
			<div className="flex w-6 flex-none flex-col items-center">
				{marker}
				{!last && <div className="w-px flex-1 bg-gray-300 dark:bg-gray-700" />}
			</div>
			<div
				className={cn(
					"flex min-w-0 flex-1 flex-col gap-1.5",
					!last && "pb-[18px]",
				)}
			>
				<div className="flex min-h-6 flex-wrap items-center gap-x-1.5 text-[13px]">
					<span
						data-step-title
						className={cn(muted ? "text-tertiary" : "font-medium text-primary")}
					>
						{title}
					</span>
					{detail && (
						<span
							className={cn(
								"min-w-0 break-words text-secondary",
								mono && "font-mono text-xs",
							)}
						>
							{detail}
						</span>
					)}
				</div>
				{children}
			</div>
		</div>
	)
}

const CHIP = "h-6 max-w-full gap-1 px-2 text-xs font-normal"
const CHIP_LINK =
	"outline-none hover:bg-gray-150 focus-visible:bg-gray-150 dark:hover:bg-gray-750 dark:focus-visible:bg-gray-750"
const CITED_LINK =
	"outline-none hover:bg-green-100 focus-visible:bg-green-100 dark:hover:bg-green-500/20 dark:focus-visible:bg-green-500/20"

function ChipLink({
	href,
	cited,
	children,
}: {
	href: string
	cited: boolean
	children: ReactNode
}) {
	return (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			data-cited={cited || undefined}
			className={badgeVariants({
				variant: cited ? "green" : "default",
				className: cn(CHIP, cited ? CITED_LINK : CHIP_LINK),
			})}
		>
			{cited && <Check className="h-3 w-3 flex-none" />}
			<span className="truncate">{children}</span>
		</a>
	)
}

const VISIBLE_RESULTS = 4

function ResultChips({
	results,
	citedDocIds,
}: {
	results: ReplyLookup["results"]
	citedDocIds: ReadonlySet<string>
}) {
	const [expanded, setExpanded] = useState(false)
	const isCited = (articleId: string | null) =>
		!!articleId && citedDocIds.has(articleId)
	// Cited articles first, so the one the reply leaned on is never hidden.
	const ordered = [...results].sort(
		(a, b) => Number(isCited(b.articleId)) - Number(isCited(a.articleId)),
	)
	const shown = expanded ? ordered : ordered.slice(0, VISIBLE_RESULTS)
	const hidden = ordered.length - shown.length
	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{shown.map((result, index) =>
				result.articleId ? (
					<ChipLink
						key={`${result.title}-${index}`}
						href={`/edit-docs/${result.articleId}`}
						cited={isCited(result.articleId)}
					>
						{result.title}
					</ChipLink>
				) : (
					<span
						key={`${result.title}-${index}`}
						className={badgeVariants({ className: CHIP })}
					>
						<span className="truncate">{result.title}</span>
					</span>
				),
			)}
			{hidden > 0 && (
				<Button size="sm" variant="ghost" onClick={() => setExpanded(true)}>
					{IMPROVE_REPLY_COPY.more(hidden)}
				</Button>
			)}
		</div>
	)
}

function LookupStep({
	lookup,
	citedDocIds,
}: {
	lookup: ReplyLookup
	citedDocIds: ReadonlySet<string>
}) {
	const heading = lookupHeading(lookup)
	return (
		<Step
			marker={
				<div
					className={cn(
						MARKER,
						!lookup.ok &&
							"text-red-600 ring-red-600/25 dark:text-red-400 dark:ring-red-400/30",
					)}
				>
					<heading.icon className="h-3.5 w-3.5 flex-none" />
				</div>
			}
			title={heading.title}
			detail={heading.detail}
			mono={heading.mono}
		>
			{!lookup.ok ? (
				<p className="break-words text-xs text-red-600 dark:text-red-400">
					{lookup.error ?? IMPROVE_REPLY_COPY.failed}
				</p>
			) : lookup.results.length === 0 ? (
				<p className="text-xs text-tertiary">{IMPROVE_REPLY_COPY.noResults}</p>
			) : (
				<ResultChips results={lookup.results} citedDocIds={citedDocIds} />
			)}
		</Step>
	)
}

const BUBBLE =
	"whitespace-pre-line break-words rounded-[10px] bg-white px-3 py-2.5 text-[13px] shadow-card dark:bg-gray-800 dark:shadow-card-dark"

function ReplyTimeline({ context }: { context: ReplyContext }) {
	const citedDocIds = new Set(
		context.sources.filter((s) => s.kind === "docs").map((s) => s.id),
	)
	const shownAsResult = new Set(
		context.lookups.flatMap((lookup) =>
			lookup.results.flatMap((r) => (r.articleId ? [r.articleId] : [])),
		),
	)
	// A source the agent opened directly never shows up as a search result.
	const otherCited = context.sources.filter(
		(source) => !(source.kind === "docs" && shownAsResult.has(source.id)),
	)
	const author = context.question?.author ?? IMPROVE_REPLY_COPY.customer
	const isEmpty = context.lookups.length === 0 && context.sources.length === 0

	return (
		<div className="flex flex-col">
			{context.question && (
				<Step
					marker={<Avatar name={author} size="s" />}
					title={`${author} ${IMPROVE_REPLY_COPY.asked}`}
				>
					<div className={cn(BUBBLE, "text-primary")}>
						{context.question.text}
					</div>
				</Step>
			)}
			{isEmpty ? (
				<Step
					marker={
						<div className={cn(MARKER, "text-tertiary")}>
							<Magnifier className="h-3.5 w-3.5 flex-none" />
						</div>
					}
					title={IMPROVE_REPLY_COPY.empty}
					muted
				/>
			) : (
				context.lookups.map((lookup, index) => (
					<LookupStep key={index} lookup={lookup} citedDocIds={citedDocIds} />
				))
			)}
			<Step
				marker={<AgentMessageAvatar />}
				title={IMPROVE_REPLY_COPY.replied}
				last={!context.handedOff}
			>
				{context.reply && (
					<div className={cn(BUBBLE, "text-secondary")}>{context.reply}</div>
				)}
				{otherCited.length > 0 && (
					<div className="flex flex-wrap gap-1.5">
						{otherCited.map((source) => (
							<ChipLink
								key={`${source.kind}:${source.id}`}
								href={sourceHref(source)}
								cited
							>
								{source.title}
							</ChipLink>
						))}
					</div>
				)}
			</Step>
			{context.handedOff && (
				<Step
					marker={
						<div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/30 dark:bg-amber-500/10 dark:text-amber-400">
							<ArrowRight className="h-3.5 w-3.5 flex-none" />
						</div>
					}
					title={IMPROVE_REPLY_COPY.handedOff}
					last
				/>
			)}
		</div>
	)
}

interface RunProposal {
	id: string
	kind: "CREATE" | "EDIT" | "DELETE"
	status: string
	articleId?: string | null
	proposedTitle?: string | null
	article?: { title?: string | null } | null
}

interface RunGroup {
	status: string
	proposals?: readonly RunProposal[]
}

export type ImproveRunStatus =
	| "running"
	| "ready"
	| "resolved"
	| "replaced"
	| "nothing"

// The group row only exists once the run persists, so its absence is the run.
export function improveRunStatus(
	group: RunGroup | null | undefined,
): ImproveRunStatus {
	if (!group) return "running"
	const proposals = group.proposals ?? []
	if (proposals.some((p) => p.status === "OPEN")) return "ready"
	if (proposals.some((p) => p.status === "REJECTED" || p.status === "ACCEPTED"))
		return "resolved"
	// A follow-up's version supersedes this draft once it lands.
	if (proposals.length > 0 && group.status === "SUPERSEDED") return "replaced"
	// Persist writes the group a moment before its children.
	return group.status === "OPEN" ? "running" : "nothing"
}

// Kept or still waiting on review: either way the reply got its fix.
export const runImprovedReply = (group: RunGroup | null | undefined) =>
	(group?.proposals ?? []).some(
		(p) => p.status === "OPEN" || p.status === "ACCEPTED",
	)

const proposalTitle = (proposal: RunProposal) =>
	proposal.proposedTitle ?? proposal.article?.title ?? "Untitled"

const RUN_CARD =
	"rounded-[10px] bg-white px-3 py-2.5 text-[13px] shadow-card dark:bg-gray-800 dark:shadow-card-dark"

function Elapsed({ since }: { since: number }) {
	const [now, setNow] = useState(() => Date.now())
	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(id)
	}, [])
	return (
		<span className="text-xs tabular-nums text-tertiary">
			{Math.max(0, Math.round((now - since) / 1000))}s
		</span>
	)
}

function TurnRun({
	turn,
	target,
}: {
	turn: ImproveReplyTurn
	target: string | null
}) {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const [group] = useQuery(queries.proposalGroupById(turn.groupId))
	const status = improveRunStatus(group)
	const proposals = (group?.proposals ?? []) as readonly RunProposal[]

	const setStatus = async (proposalId: string, skip: boolean) => {
		try {
			await mutateOk(
				zero.mutate(
					skip
						? mutators.helpCenterArticleProposals.reject({ id: proposalId })
						: mutators.helpCenterArticleProposals.reopen({ id: proposalId }),
				),
			)
		} catch (error) {
			captureError(error, {
				domain: "ai-chat",
				action: "skip docs change from improve reply",
				workspaceId: workspace?.id,
			})
			toast.error({
				title: IMPROVE_REPLY_COPY.skipFailed,
				description: error instanceof Error ? error.message : undefined,
				icon: IconBookOpen4Fill18,
			})
		}
	}

	if (status === "running") {
		return (
			<div
				data-testid="improve-reply-run"
				data-status={status}
				className={cn(RUN_CARD, "flex items-center gap-2.5")}
			>
				<Loader className="h-3.5 w-3.5 flex-none" />
				<span className="min-w-0 flex-1 truncate font-medium text-primary">
					{target
						? IMPROVE_REPLY_COPY.editing(target)
						: IMPROVE_REPLY_COPY.draftingDocs}
				</span>
				<Elapsed since={turn.startedAt} />
			</div>
		)
	}

	if (status === "replaced" || status === "nothing") {
		return (
			<p
				data-testid="improve-reply-run"
				data-status={status}
				className="text-xs text-tertiary"
			>
				{status === "replaced"
					? IMPROVE_REPLY_COPY.replaced
					: IMPROVE_REPLY_COPY.noChange}
			</p>
		)
	}

	const closed = proposals.filter(
		(p) => p.status === "REJECTED" || p.status === "ACCEPTED",
	)
	return (
		<div
			data-testid="improve-reply-run"
			data-status={status}
			className="flex flex-col gap-1"
		>
			<DocsProposalCards
				changes={docsProposalChanges(proposals)}
				groupId={turn.groupId}
				onSkip={(id) => void setStatus(id, true)}
			/>
			{closed.map((proposal) => (
				<div
					key={proposal.id}
					data-testid="improve-reply-closed-change"
					className={cn(RUN_CARD, "flex items-center gap-2")}
				>
					<span className="min-w-0 flex-1 truncate text-primary">
						{proposalTitle(proposal)}
					</span>
					<span className="text-xs text-tertiary">
						{proposal.status === "REJECTED"
							? IMPROVE_REPLY_COPY.skipped
							: IMPROVE_REPLY_COPY.published}
					</span>
					{proposal.status === "REJECTED" && (
						<Button
							size="sm"
							variant="ghost"
							onClick={() => void setStatus(proposal.id, false)}
						>
							{IMPROVE_REPLY_COPY.undo}
						</Button>
					)}
				</div>
			))}
		</div>
	)
}

function useAddRule(onAdded: () => void) {
	const navigate = useNavigate()
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const settings = workspace?.aiChatSettings
	const [saving, setSaving] = useState<string | null>(null)

	const addRule = async (text: string) => {
		const next = text.trim()
		if (!next || !settings || saving) return
		setSaving(text)
		try {
			const current = settings.customInstructions?.trimEnd() ?? ""
			await mutateOk(
				zero.mutate(
					mutators.aiChatSettings.update({
						id: settings.id,
						customInstructions: current ? `${current}\n${next}` : next,
					}),
				),
			)
		} catch (error) {
			captureError(error, {
				domain: "ai-chat",
				action: "add guidance rule from improve reply",
				workspaceId: workspace?.id,
			})
			toast.error({
				title: IMPROVE_REPLY_COPY.ruleFailed,
				description: error instanceof Error ? error.message : undefined,
				icon: IconTextAlignLeftFill18,
			})
			return false
		} finally {
			setSaving(null)
		}
		onAdded()
		toast.success({
			title: IMPROVE_REPLY_COPY.ruleAdded,
			icon: IconTextAlignLeftFill18,
			action: {
				label: IMPROVE_REPLY_COPY.seeGuidance,
				callback: () => void navigate({ to: "/settings/ai/agent" }),
			},
		})
		return true
	}

	return { canAddRule: !!settings, addRule, saving }
}

function useImproveChat({
	threadId,
	channel,
	messageId,
	context,
}: {
	threadId: string
	channel: AgentReplyChannel
	messageId: string
	context: ReplyContext | undefined
}) {
	const workspace = useZeroWorkspace()
	const turns = useImproveReplyTurns(messageId)
	const addTurn = useImproveReplyTurnsState((s) => s.addTurn)
	const markImproved = useImprovedRepliesState((s) => s.markImproved)
	const draftDocs = api.agentReplies.draftDocs.useMutation()
	const { canAddRule, addRule, saving } = useAddRule(() =>
		markImproved(messageId),
	)
	const answerRef = useRef<HTMLTextAreaElement>(null)
	const [answer, setAnswer] = useState("")
	const [edited, setEdited] = useState(false)
	const [pending, setPending] = useState<string | null>(null)
	const [ruled, setRuled] = useState<ReadonlySet<string>>(() => new Set())
	const endRef = useRef<HTMLDivElement>(null)

	// The trace is long, so a new message (or a reopen mid-run) scrolls to the chat.
	useEffect(() => {
		if (turns.length === 0 && !pending) return
		endRef.current?.scrollIntoView?.({ block: "end" })
	}, [turns.length, pending])

	// A teammate's own answer after the agent's is the fix, until someone edits it.
	const teamReply = context?.teamReply
	useEffect(() => {
		if (teamReply && !edited && turns.length === 0) setAnswer(teamReply.text)
	}, [teamReply, edited, turns.length])

	// The same article the server targets, so the running card names it.
	const target =
		context?.sources.find((source) => source.kind === "docs")?.title ?? null

	const send = async () => {
		const text = answer.trim()
		if (!text || pending) return
		setPending(text)
		setAnswer("")
		setEdited(true)
		try {
			const { proposalGroupId } = await draftDocs.mutateAsync({
				threadId,
				channel,
				messageId,
				answer: text,
				earlier: turns.map((turn) => turn.text),
				replacesGroupId: turns.at(-1)?.groupId,
			})
			addTurn(messageId, {
				text,
				groupId: proposalGroupId,
				startedAt: Date.now(),
			})
		} catch (error) {
			captureError(error, {
				domain: "ai-chat",
				action: "draft docs fix from improve reply",
				workspaceId: workspace?.id,
				extra: { threadId, channel, messageId },
			})
			toast.error({
				title: IMPROVE_REPLY_COPY.draftFailed,
				description: error instanceof Error ? error.message : undefined,
				icon: IconBookOpen4Fill18,
			})
			setAnswer(text)
		} finally {
			setPending(null)
			answerRef.current?.focus()
		}
	}

	const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key !== "Enter" || event.shiftKey) return
		if (event.nativeEvent.isComposing) return
		event.preventDefault()
		void send()
	}

	const bubble = (text: string) => (
		<div
			data-testid="improve-reply-message"
			className="ml-8 self-end whitespace-pre-line break-words rounded-[14px] rounded-br-[4px] bg-[var(--text-primary)] px-3 py-2 text-[13px] text-gray-50 dark:text-gray-900"
		>
			{text}
		</div>
	)

	return {
		thread: (
			<div className="mt-5 flex flex-col gap-3 border-t border-gray-200 pt-4 dark:border-gray-700/80">
				<p className="text-[13px] text-primary">{IMPROVE_REPLY_COPY.ask}</p>
				{turns.map((turn) => (
					<div key={turn.groupId} className="flex flex-col gap-2">
						{bubble(turn.text)}
						{canAddRule && (
							<div className="-mt-1 flex justify-end">
								<Button
									size="sm"
									variant="ghost"
									isLoading={saving === turn.text}
									disabled={ruled.has(turn.text) || !!saving}
									Icon={ruled.has(turn.text) ? Check : undefined}
									onClick={() =>
										void addRule(turn.text).then((added) => {
											if (added) setRuled(new Set(ruled).add(turn.text))
										})
									}
								>
									{IMPROVE_REPLY_COPY.addRule}
								</Button>
							</div>
						)}
						<TurnRun turn={turn} target={target} />
					</div>
				))}
				{pending && (
					<div className="flex flex-col gap-2">
						{bubble(pending)}
						<div className={cn(RUN_CARD, "flex items-center gap-2.5")}>
							<Loader className="h-3.5 w-3.5 flex-none" />
							<span className="font-medium text-primary">
								{target
									? IMPROVE_REPLY_COPY.editing(target)
									: IMPROVE_REPLY_COPY.draftingDocs}
							</span>
						</div>
					</div>
				)}
				<div ref={endRef} />
			</div>
		),
		composer: (
			<div className="flex-none px-3 pb-3 pt-3">
				<div className="flex items-end gap-2 rounded-xl bg-white py-1.5 pl-1 pr-2 ring-1 ring-gray-900/15 focus-within:ring-blue-400 dark:bg-gray-900 dark:ring-white/15">
					<Textarea
						ref={answerRef}
						name="improve-reply-answer"
						aria-label={IMPROVE_REPLY_COPY.ask}
						variant="borderless"
						className="min-w-0 flex-1"
						inputClassName="px-2.5 py-1.5 leading-normal"
						value={answer}
						placeholder={
							turns.length > 0
								? IMPROVE_REPLY_COPY.followUpPlaceholder
								: IMPROVE_REPLY_COPY.answerPlaceholder
						}
						minRows={3}
						maxRows={14}
						onKeyDown={onKeyDown}
						onChange={(event) => {
							setAnswer(event.target.value)
							setEdited(true)
						}}
					/>
					<IconButton
						variant="solid"
						Icon={ArrowUp}
						aria-label={IMPROVE_REPLY_COPY.send}
						disabled={!answer.trim() || !!pending}
						onClick={() => void send()}
						className="mb-0.5"
					/>
				</div>
			</div>
		),
	}
}

export function ImproveReplyPanel({
	open,
	onOpenChange,
	threadId,
	channel,
	messageId,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	threadId: string
	channel: AgentReplyChannel
	messageId: string
}) {
	const { role } = useRole()
	const canEdit = !!role && role !== "VIEWER"

	const contextQuery = api.agentReplies.context.useQuery(
		{ threadId, channel, messageId },
		{ enabled: open, staleTime: 60_000, retry: 1 },
	)
	const context = contextQuery.data

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="right"
				hideCloseButton
				isOverlayVisible
				overlayClassName="bg-gray-950/20 backdrop-blur-none dark:bg-gray-950/50"
				aria-describedby={undefined}
				onOpenAutoFocus={(event) => event.preventDefault()}
				data-testid="improve-reply-panel"
				className={cn(
					"flex w-full flex-col gap-0 border-l-0 bg-gray-100 p-0 sm:max-w-[30rem] dark:bg-gray-850",
					"inset-y-1 h-auto overflow-hidden rounded-l-2xl lg:inset-y-2",
					"shadow-xl ring-1 ring-black/10 dark:shadow-black dark:ring-white/15",
					"transition-none",
				)}
			>
				<div className="flex h-12 flex-none items-center justify-between border-b border-gray-200 pl-4 pr-2 dark:border-gray-700/80">
					<div className="flex min-w-0 flex-col">
						<SheetTitle className="text-sm font-medium text-primary">
							{IMPROVE_REPLY_COPY.title}
						</SheetTitle>
						{/* Reserved so the title holds still while the summary loads. */}
						<span
							className="h-4 truncate text-xs leading-4 text-tertiary"
							data-testid="improve-reply-summary"
						>
							{context ? summarizeReply(context) : ""}
						</span>
					</div>
					<SheetClose asChild>
						<IconButton Icon={Xmark} variant="ghost" aria-label="Close" />
					</SheetClose>
				</div>

				{canEdit ? (
					<EditableBody
						threadId={threadId}
						channel={channel}
						messageId={messageId}
						contextQuery={contextQuery}
					/>
				) : (
					<div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-4">
						<TraceBody contextQuery={contextQuery} />
					</div>
				)}
			</SheetContent>
		</Sheet>
	)
}

interface ContextQuery {
	data: ReplyContext | undefined
	isLoading: boolean
	isError: boolean
}

function TraceBody({ contextQuery }: { contextQuery: ContextQuery }) {
	if (contextQuery.isLoading) {
		return (
			<div className="flex h-32 items-center justify-center text-tertiary">
				<Loader />
			</div>
		)
	}
	if (contextQuery.isError || !contextQuery.data) {
		return (
			<p className="py-2 text-sm text-secondary">
				{IMPROVE_REPLY_COPY.loadFailed}
			</p>
		)
	}
	return <ReplyTimeline context={contextQuery.data} />
}

function EditableBody({
	threadId,
	channel,
	messageId,
	contextQuery,
}: {
	threadId: string
	channel: AgentReplyChannel
	messageId: string
	contextQuery: ContextQuery
}) {
	const chat = useImproveChat({
		threadId,
		channel,
		messageId,
		context: contextQuery.data,
	})
	return (
		<>
			<div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-4">
				<TraceBody contextQuery={contextQuery} />
				{chat.thread}
			</div>
			{chat.composer}
		</>
	)
}

// A fix finishes while someone works another thread, so the result waits for them.
const RUN_TOAST_MS = 5 * 60_000

// The loader in the button's icon slot, sized and coloured like the icon it replaces.
const DraftingLoader: Icon = ({ className, style }) => (
	<span
		className={cn(className, "inline-flex items-center justify-center")}
		style={style}
	>
		<Loader size={4} />
	</span>
)

export function ImproveReplyButton({
	threadId,
	channel,
	messageId,
}: {
	threadId: string
	channel: AgentReplyChannel
	messageId: string
}) {
	const [open, setOpen] = useState(false)
	const improved = useImprovedRepliesState((s) =>
		s.improvedMessageIds.includes(messageId),
	)
	const markImproved = useImprovedRepliesState((s) => s.markImproved)
	const latest = useImproveReplyTurns(messageId).at(-1)
	// Watched here, not in the panel, so a run that ends while it is closed still counts.
	const [runGroup, runResult] = useQuery(
		queries.proposalGroupById(latest?.groupId ?? ""),
		{
			enabled: !!latest,
		},
	)
	// Before Zero answers, a missing row reads as running, so a reload would toast every finished run.
	const status =
		latest && runResult.type === "complete" ? improveRunStatus(runGroup) : null
	const running = status === "running"
	const drafted = runImprovedReply(runGroup)
	useEffect(() => {
		if (drafted && !improved) markImproved(messageId)
	}, [drafted, improved, markImproved, messageId])

	const sawRunning = useRef(false)
	useEffect(() => {
		if (status === "running") {
			sawRunning.current = true
			return
		}
		if (!status || !sawRunning.current) return
		sawRunning.current = false
		// The open panel already shows the result.
		if (open) return
		if (status === "ready") {
			toast.success({
				title: IMPROVE_REPLY_COPY.fixReady,
				icon: IconBookOpen4Fill18,
				time: RUN_TOAST_MS,
				action: {
					label: IMPROVE_REPLY_COPY.open,
					callback: () => setOpen(true),
				},
			})
		} else if (status === "nothing") {
			toast.info({
				title: IMPROVE_REPLY_COPY.noChange,
				icon: IconBookOpen4Fill18,
				time: RUN_TOAST_MS,
			})
		}
	}, [status, open])

	return (
		<>
			<Button
				variant={improved && !running ? "green-subtle" : "outline-blue"}
				size="sm"
				// Not isLoading: that disables the button, and a running fix must stay openable.
				Icon={
					running
						? DraftingLoader
						: improved
							? Check
							: IconChatBubbleSparkleOutline18
				}
				onClick={() => setOpen(true)}
			>
				{running
					? IMPROVE_REPLY_COPY.drafting
					: improved
						? IMPROVE_REPLY_COPY.improved
						: IMPROVE_REPLY_COPY.button}
			</Button>
			<ImproveReplyPanel
				open={open}
				onOpenChange={setOpen}
				threadId={threadId}
				channel={channel}
				messageId={messageId}
			/>
		</>
	)
}
